import { ReactNode } from 'react';
// 组件
import { SortableHandle } from '@yonboo/react-sortable-hoc';
import { DeleteOutlined, MenuOutlined } from '@ant-design/icons';
import { ProButton, ProTooltip, ProImage } from '../../antd';
import CopyText from '../../CopyText';
import SkuInfoList from '../../SkuInfoList';
import Images from '../../Images';
// 方法
import usePrevious from '../../Hooks/usePrevious';
import { IEnum } from '../../PageLayout/context/LayoutContext';
import { renderEnumNames } from '../../utils/renderUtils';
import { getColumnValue } from '../../utils/dataUtils';
// 常量
import skuInfoListConfig from '../../constants/skuInfoListConfig';
import { TABLE_COLUMN_EMPTY } from '../../constants/constant';
import { ColumnType } from 'antd/lib/table';
import {
  EdiTableProps,
  EditColumnType,
  EdiTableDispatchType,
  IEdiTablePayload,
} from '../interface';

const DragHandle = SortableHandle(() => <MenuOutlined />);
function useEdiTableColumnsHook(
  props: EdiTableProps,
  dispatchEdiTable: (
    type: EdiTableDispatchType,
    payload: IEdiTablePayload,
  ) => void,
  getDataSource: () => void,
  tableElement: HTMLElement | null,
  enumData?: IEnum
) {
  const {
    columns,
    disabled,
    hideAdd,
    addIcon,
    operation,
    sort,
    showSortColumn,
    moveSort,
    mergeRowKey,
    operationTitle,
    operationWidth,
    rowRender,
    onAdd,
    onDelete,
  } = props;
  const prevDisabled = usePrevious(disabled);
  const handleAdd = () => {
    const newRowData = onAdd ? onAdd() : {};
    if (!newRowData) return;
    getDataSource();
    dispatchEdiTable('addRow', { addItem: newRowData });
  };
  const handleDelete = async (index: number, record: any) => {
    if (onDelete) {
      const result = await onDelete(index, record);
      if (!result) return;
    }
    getDataSource();
    dispatchEdiTable('deleteRow', { index: record.key });
  };
  const formatColumns = (
    columnsProps: EditColumnType[],
    isChildren: boolean = false,
  ): EditColumnType[] => {
    let newColumns: EditColumnType[] = [];
    if (Array.isArray(columnsProps) && columnsProps.length) {
      const baseColumnsProps: EditColumnType = {
        ellipsis: {
          showTitle: false,
        },
      };
      // @ts-ignore
      newColumns = columnsProps?.map((column, index) => {
        const {
          editable,
          title,
          dataIndex,
          enumCode,
          renderType,
          children,
          fixed,
          required,
          relativePathName,
          disabled: columnDisabled,
          skuInfoListProps,
          ...restProps
        } = column;
        // const baseUpdateColumnsProps: EditColumnType = {};
        const baseUpdateColumnsProps: EditColumnType = fixed
          ? {}
          : {
              shouldCellUpdate: (record, prevRecord) => {
                if (!dataIndex) return true;
                if (record.isMainTable) return true;
                if (disabled !== prevDisabled) return true;
                if (typeof dataIndex === 'string') {
                  return record[dataIndex] !== prevRecord[dataIndex];
                }
                return false;
              },
            };
        if (editable) {
          return {
            ...baseColumnsProps,
            ...column,
            ...baseUpdateColumnsProps,
            title: required ? (
              <div className="edit-table-column-required">{title as ReactNode}</div>
            ) : (
              title
            ),
            render: (val: any, record: any) => {
              if (record.isMainTable && mergeRowKey) {
                return renderMainRow(record, index, mergeRowKey);
              }
              return val;
            },
            onCell: (record: any, recordIndex?: number) => {
              let initialValue: any;
              if (typeof dataIndex === 'string')
                initialValue = record[dataIndex];
              if (
                (restProps.formItemType === 'SelectTable' || restProps.formItemType === 'SuperSelectTable') &&
                Array.isArray(dataIndex)
              ) {
                initialValue = record[dataIndex[0]];
              }
              if (!initialValue && restProps.initialValue !== undefined)
                initialValue = restProps.initialValue;
              return {
                record,
                editable,
                disabled,
                mergeRowKey,
                columnIndex: index,
                rowIndex: recordIndex,
                rowRender,
                tableElement,
                formItemProps: {
                  relativePathName,
                  enumCode,
                  required,
                  label: title,
                  name: dataIndex,
                  disabled: disabled || (typeof columnDisabled === 'function' ? columnDisabled(record) : columnDisabled),
                  ...restProps,
                  initialValue,
                },
              };
            },
          };
        }
        if (enumCode && enumData) {
          const enumItem = enumData[`${enumCode}`];
          return enumItem
            ? {
                ...baseColumnsProps,
                ...column,
                render: (_: any, record: any) => {
                  const columnValue = getColumnValue(record, dataIndex);
                  if (record.isMainTable && mergeRowKey) {
                    return renderMainRow(record, index, mergeRowKey);
                  }
                  return renderEnumNames(columnValue, enumItem.items);
                },
              }
            : { ...baseColumnsProps, ...column };
        }
        if (Array.isArray(dataIndex)) {
          return {
            ...baseColumnsProps,
            ...column,
            render: (_: any, record: any) => {
              if (record.isMainTable && mergeRowKey) {
                return renderMainRow(record, index, mergeRowKey);
              }
              return (
                <>
                  {dataIndex.map(item => {
                    const columnValue = getColumnValue(record, item);
                    if (renderType === 'copy') {
                      return <CopyText text={columnValue} isBlock key={item}/>;
                    }
                    return (
                      <ProTooltip
                        key={item}
                        placement="topLeft"
                        title={columnValue}
                        mouseEnterDelay={1}
                      >
                        <div className="ant-table-cell-ellipsis">
                          {columnValue || TABLE_COLUMN_EMPTY}
                        </div>
                      </ProTooltip>
                    );
                  })}
                </>
              );
            },
          };
        }
        switch (renderType) {
          case 'skuInfoList':
          const skuInfoListWidth = Number(column.width || 600);
          return {
            ...baseColumnsProps,
            ...column,
            width: skuInfoListWidth + 10,
            render: (_: string, record: any) => {
              const columnValue = getColumnValue(record, dataIndex);
              return (
                <SkuInfoList
                  data={columnValue}
                  width={skuInfoListWidth}
                  {...skuInfoListConfig}
                  {...skuInfoListProps}
                />
              );
            },
          };
          case 'image':
            return {
              ...baseColumnsProps,
              className: 'table-image-column',
              ...column,
              render: (_: any, record: any) => {
                const columnValue = getColumnValue(record, dataIndex);
                if (record.isMainTable && mergeRowKey) {
                  return renderMainRow(record, index, mergeRowKey);
                }
                return <ProImage className="table-column-image" src={columnValue} />;
              },
            };
          case 'images':
            return {
              ...column,
              width: column.width || 280,
              render: (_: any, record: any) => {
                const columnValue = getColumnValue(record, dataIndex);
                if (record.isMainTable && mergeRowKey) {
                  return renderMainRow(record, index, mergeRowKey);
                }
                if (Array.isArray(columnValue)) return <Images images={columnValue} />;
                return null;
              },
            };
          case 'boolean':
            return {
              ...baseColumnsProps,
              ...column,
              render: (_: any, record: any) => {
                const columnValue = getColumnValue(record, dataIndex);
                if (record.isMainTable && mergeRowKey) {
                  return renderMainRow(record, index, mergeRowKey);
                }
                return columnValue ? '是' : '否';
              },
            };
          case 'copy':
            return {
              ...baseColumnsProps,
              ...column,
              render: (_: any, record: any, key: number) => {
                const columnValue = getColumnValue(record, dataIndex);
                if (record.isMainTable && mergeRowKey) {
                  return renderMainRow(record, index, mergeRowKey);
                }
                return <CopyText text={columnValue} align={column.align} />;
              },
            };
        }
        return {
          ...baseColumnsProps,
          ...baseUpdateColumnsProps,
          ...column,
          children: children ? formatColumns(children, true) : undefined,
          render: (_: any, record: any, key: number) => {
            const columnValue = getColumnValue(record, dataIndex);
            if (record.isMainTable && mergeRowKey) {
              return renderMainRow(record, index, mergeRowKey);
            }
            if (columnValue === undefined || columnValue === '' || columnValue === null) {
              return column.render
                ? column.render(columnValue, record, key)
                : TABLE_COLUMN_EMPTY;
            }
            if (typeof columnValue === 'string') {
              // @ts-ignore
              const columnHTML: ReactNode = column.render ? column.render(columnValue, record, key) : columnValue;
              return (
                <ProTooltip placement="topLeft" title={columnValue}>
                  {columnHTML}
                </ProTooltip>
              );
            }
            return column.render ? column.render(columnValue, record, key) : columnValue;
          },
        };
      });
    }
    if (isChildren) return newColumns;
    if (operation !== false && !disabled) {
      const operationColumns: ColumnType<any> = operation || {
        fixed: 'right',
        width: operationWidth,
        align: 'center',
        title: hideAdd ? null : (operationTitle ? operationTitle : (
          <ProButton type="primary" size="small" onClick={handleAdd}>
            {addIcon}
          </ProButton>
        )),
        render: (text: any, record: any, key: number) => {
          if (record.isMainTable) {
            return {
              children: '',
              props: {
                colSpan: 0,
                className: 'yb-table-row-main',
              },
            };
          }
          return (
            <>
              <DeleteOutlined
                className='text-danger'
                onClick={() => handleDelete(key, record)}
                title="删除"
              />
            </>
          );
        },
      };
      // @ts-ignore
      newColumns.push(operationColumns);
    }
    if ((moveSort !== false || sort) && showSortColumn) {
      newColumns.unshift({
        title: '序号',
        width: 80,
        fixed: 'left',
        render: (val: any, record: any, key: number) => {
          return key + 1;
        },
      });
    }
    if (moveSort !== false && !disabled) {
      newColumns.unshift({
        title: '排序',
        // @ts-ignore
        dataIndex: 'sort',
        width: 80,
        className: 'drag-visible',
        fixed: 'left',
        render: () => <DragHandle />,
        ...moveSort,
      });
    }
    return newColumns;
  };
  const renderMainRow = (
    record: any,
    index: number,
    mergeDataIndex: string,
  ) => {
    if (!columns) return;
    return {
      children: rowRender ? rowRender(record) : record[mergeDataIndex],
      props: {
        colSpan: index === 0 ? columns.length : 0,
        className: 'yb-table-row-main',
      },
    };
  };
  return {
    columns: formatColumns(columns || []),
  };
}

export default useEdiTableColumnsHook;
